import { Question, UserResponse, Subject } from '../types';
import { API_CONFIG, DEV_CONFIG } from '../config/api';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  context?: {
    currentQuestion?: Question;
    userPerformance?: {
      accuracy: number;
      speed: number;
      recentErrors: string[];
    };
  };
}

export interface ChatContext {
  currentQuestion: Question | null;
  previousQuestions: Question[];
  userResponses: UserResponse[];
  subject: Subject;
  timeSpentOnCurrentQuestion: number;
  userStrengths: string[];
  userWeaknesses: string[];
}

export class ChatService {
  private messages: ChatMessage[] = [];

  constructor() {
    // 添加欢迎消息
    this.addWelcomeMessage();
  }

  private addWelcomeMessage(): void {
    const welcomeMessage: ChatMessage = {
      id: 'welcome_' + Date.now(),
      role: 'assistant',
      content: '你好！我是你的AI学习伴侣 🤖 \n\n在答题过程中，你可以随时和我聊天：\n• 遇到困难时寻求帮助\n• 讨论解题思路\n• 询问学习建议\n• 或者只是聊聊学习感受\n\n我会根据你的学习表现给出个性化的指导。开始答题吧！',
      timestamp: Date.now()
    };
    this.messages.push(welcomeMessage);
  }

  async sendMessage(
    userMessage: string, 
    context: ChatContext
  ): Promise<ChatMessage> {
    // 添加用户消息
    const userChatMessage: ChatMessage = {
      id: 'user_' + Date.now(),
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
      context: {
        currentQuestion: context.currentQuestion || undefined,
        userPerformance: this.calculateUserPerformance(context)
      }
    };
    this.messages.push(userChatMessage);

    // 生成AI回复
    const aiResponse = await this.generateAIResponse(userMessage, context);
    
    const aiChatMessage: ChatMessage = {
      id: 'assistant_' + Date.now(),
      role: 'assistant', 
      content: aiResponse,
      timestamp: Date.now()
    };
    this.messages.push(aiChatMessage);

    return aiChatMessage;
  }

  private async generateAIResponse(
    userMessage: string,
    context: ChatContext
  ): Promise<string> {
    if (DEV_CONFIG.USE_MOCK_AI || !API_CONFIG.DEEPSEEK.API_KEY) {
      return this.generateMockResponse(userMessage, context);
    }

    try {
      const prompt = this.buildChatPrompt(userMessage, context);
      
      const response = await fetch(API_CONFIG.DEEPSEEK.BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_CONFIG.DEEPSEEK.API_KEY}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `你是一个专业、温暖、有耐心的AI学习伴侣。你的角色是：

🎯 核心职责：
- 提供个性化的学习指导和情感支持
- 基于用户的学习表现给出针对性建议
- 鼓励用户，帮助建立学习信心
- 解答学习中的困惑，讨论解题思路

💬 对话风格：
- 亲切友好，像朋友一样交流
- 用简单易懂的语言解释复杂概念
- 积极正面，善于发现用户的进步
- 适度使用emoji让对话更生动

📚 专业能力：
- 深度理解${context.subject === 'math' ? '数学' : '语文'}学科知识
- 根据用户答题情况分析学习模式
- 提供具体可操作的学习建议
- 帮助用户理解而不是直接给答案

🚫 注意事项：
- 不直接透露题目答案
- 用引导方式帮助用户思考
- 回复控制在100字以内，简洁有效
- 用中文回答`
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.8,
          max_tokens: 300
        })
      });

      const data = await response.json();
      const aiResponse = data.choices[0]?.message?.content;

      return aiResponse || this.generateMockResponse(userMessage, context);
    } catch (error) {
      console.error('AI对话生成失败:', error);
      return this.generateMockResponse(userMessage, context);
    }
  }

  private buildChatPrompt(userMessage: string, context: ChatContext): string {
    const performance = this.calculateUserPerformance(context);
    
    return `用户说："${userMessage}"

📋 当前学习情况：
- 学科：${context.subject === 'math' ? '数学' : '语文'}
- 当前题目：${context.currentQuestion ? context.currentQuestion.content.title : '暂无'}
- 题目难度：${context.currentQuestion ? context.currentQuestion.difficulty + '/5' : '未知'}
- 答题时间：${Math.round(context.timeSpentOnCurrentQuestion / 1000)}秒
- 正确率：${(performance.accuracy * 100).toFixed(0)}%
- 答题速度：${performance.speed < 1 ? '较快' : performance.speed > 1.5 ? '较慢' : '中等'}

👤 用户特点：
- 强项：${context.userStrengths.join('、') || '正在分析中'}
- 弱项：${context.userWeaknesses.join('、') || '暂无明显弱项'}
- 最近错误：${performance.recentErrors.join('、') || '表现良好'}

请基于以上信息，给出温暖、专业、个性化的回复。重点：
1. 理解用户的情绪和需求
2. 基于学习数据给出针对性建议  
3. 鼓励用户，增强学习信心
4. 如果用户询问当前题目，给思路不给答案

回复要求：简洁有效，100字以内。`;
  }

  private calculateUserPerformance(context: ChatContext): {
    accuracy: number;
    speed: number;
    recentErrors: string[];
  } {
    const responses = context.userResponses;
    if (responses.length === 0) {
      return {
        accuracy: 0.5,
        speed: 1.0,
        recentErrors: []
      };
    }

    const correctAnswers = responses.filter(r => r.is_correct).length;
    const accuracy = correctAnswers / responses.length;

    let totalSpeedRatio = 0;
    let validSpeedCount = 0;
    const recentErrors: string[] = [];
    
    responses.forEach((response, index) => {
      const question = context.previousQuestions[index];
      if (question && response) { // 添加安全检查
        const timeLimit = question.time_limit || 60;
        const speedRatio = response.time_spent / (timeLimit * 1000);
        totalSpeedRatio += Math.min(speedRatio, 2);
        validSpeedCount++;

        if (!response.is_correct) {
          recentErrors.push(question.content.title.slice(0, 20) + '...');
        }
      }
    });

    const speed = validSpeedCount > 0 ? totalSpeedRatio / validSpeedCount : 1.0;

    return {
      accuracy,
      speed,
      recentErrors: recentErrors.slice(-3) // 最近3个错误
    };
  }

  private generateMockResponse(userMessage: string, context: ChatContext): string {
    const userMessageLower = userMessage.toLowerCase();
    
    // 情感支持类回复
    if (userMessageLower.includes('难') || userMessageLower.includes('不会') || userMessageLower.includes('困难')) {
      const encouragements = [
        '别担心！每个人学习都会遇到困难，这很正常。让我们一起来分析这道题，找到突破点。你觉得哪个部分最困惑呢？🤗',
        '我看得出你很认真在思考！遇到难题说明你在挑战自己的极限。我们可以把这道题分解成几个小步骤，一步步来解决。',
        '从你之前的表现看，你的基础很扎实！这道题可能只是需要换个角度思考。试试先理解题目要求什么？',
        '学习就像爬山，越往上越有挑战。但你已经走了这么远，说明你有这个能力！我相信你能克服这个困难。💪'
      ];
      return encouragements[Math.floor(Math.random() * encouragements.length)];
    }

    // 询问解题方法
    if (userMessageLower.includes('怎么') || userMessageLower.includes('如何') || userMessageLower.includes('方法')) {
      if (context.subject === 'math') {
        return '解数学题我有个小技巧分享：先看题目类型，再回忆相关公式，然后列出已知条件。你可以试试先告诉我这道题的已知条件是什么？';
      } else {
        return '语文题要善用"三步法"：1）仔细读题找关键词，2）联系已学知识，3）排除明显错误选项。你觉得这道题的关键词是什么？';
      }
    }

    // 询问学习建议
    if (userMessageLower.includes('建议') || userMessageLower.includes('提高') || userMessageLower.includes('学习')) {
      const performance = this.calculateUserPerformance(context);
      if (performance.speed > 1.5) {
        return '我注意到你做题很仔细，这是很好的习惯！不过可以适当加快速度，先快速理解题意，再细心计算。质量和效率并重哦！⚡';
      } else if (performance.accuracy < 0.6) {
        return '你的答题速度不错！建议在准确性上再加强一下，可以做完题后简单检查一遍。慢一点点但更准确，会让你更有信心。✅';
      } else {
        return '你的学习状态很好！保持现在的节奏，可以适当挑战更难的题目。记住，每一次思考都是在成长！🌟';
      }
    }

    // 表达沮丧情绪
    if (userMessageLower.includes('错') || userMessageLower.includes('失败') || userMessageLower.includes('笨')) {
      return '嘿，别这样说自己！错误是学习最好的老师。从数据看，你在很多方面都在进步。每个错误都让你更接近正确答案，这是学习的必经之路！🚀';
    }

    // 询问当前题目
    if (userMessageLower.includes('这道题') || userMessageLower.includes('这题') || (context.currentQuestion && userMessageLower.includes('题'))) {
      if (context.currentQuestion) {
        if (context.subject === 'math') {
          return '这道数学题考查的是你的逻辑思维。我不会直接告诉你答案，但可以提示：仔细观察数字之间的关系，或者试试画个图帮助理解。你发现什么规律了吗？';
        } else {
          return '这道语文题需要理解深层含义。建议你先理解每个选项的意思，再想想哪个最符合题目要求。语文很多时候靠语感，相信你的第一直觉！';
        }
      } else {
        return '现在还没有具体题目哦！开始答题后，你可以随时问我关于题目的问题，我会给你思路指导。';
      }
    }

    // 默认鼓励回复
    const defaultReplies = [
      '我一直在这里陪伴你学习！有任何问题都可以问我，不管是学习上的困难还是想聊聊学习感受。你是怎么想的呢？😊',
      '学习路上有我陪伴！我看到你一直在努力，这让我很欣慰。继续加油，你一定能达到自己的目标！',
      '很高兴和你一起学习！从你的表现看，你是个很有潜力的学习者。有什么想讨论的吗？',
      '我注意到你在学习中很认真投入，这种态度很棒！遇到任何问题都可以和我交流，我们一起解决。'
    ];
    
    return defaultReplies[Math.floor(Math.random() * defaultReplies.length)];
  }

  getMessages(): ChatMessage[] {
    return [...this.messages];
  }

  clearMessages(): void {
    this.messages = [];
    this.addWelcomeMessage();
  }

  // 根据答题情况更新上下文，给出主动建议
  async getContextualSuggestion(context: ChatContext): Promise<string | null> {
    const performance = this.calculateUserPerformance(context);
    
    // 连续答错时主动鼓励
    if (context.userResponses.length >= 2) {
      const lastTwoResponses = context.userResponses.slice(-2);
      if (lastTwoResponses.every(r => !r.is_correct)) {
        return '我注意到你遇到了一些困难，这很正常！每个人都有这样的时刻。要不要暂停一下，我们聊聊解题思路？💪';
      }
    }

    // 表现突出时给予肯定
    if (context.userResponses.length >= 3 && performance.accuracy >= 0.8 && performance.speed < 1.2) {
      return '哇！你的表现真棒！既准确又高效，看来你已经找到了适合自己的答题节奏。继续保持这种状态！🌟';
    }

    // 在某道题停留时间过长
    if (context.timeSpentOnCurrentQuestion > 45000) { // 45秒
      return '我看到你在这道题上思考了很久，这说明你很认真！如果遇到困难，可以问我要一些思路提示哦~';
    }

    return null;
  }
}